import {
  Correction,
  DcSwitch,
  GlassSurfaceSwitch,
  KeyDebounceTime,
  KeyReleaseDebounceTime,
  KeyScanRate,
  LowLightEffectSwitch,
  MotionSynSwitch,
  MousePerfCfg,
  ReportRate,
  RfBoostSwitch,
  RfPacketType,
  SensorAngle,
  SensorMode,
  SensorModes,
  SilentHeight,
  SleepTime,
} from '../../types/mousePerfCfg'
import { FeatureBase } from '../FeatureBase'

// 扩展功能注册表
declare module '../FeatureMap' {
  export interface DeviceFeatureMap {
    mousePerfCfg: IMousePerfCfgFeature
  }
}

export interface IMousePerfCfgFeature {
  /**
   * 回报率协议值按传感器模式硬件槽位排列的顺序表，下标即 `getSensorMode` / `setSensorMode`
   * 的 index 参数。由各芯片实现提供，应用层用 `indexOf(rate)` 求槽位，不要自行猜测顺序。
   */
  readonly reportRateSlots: readonly ReportRate[]
  // 获取通讯协议设置
  getRFProtocol(): Promise<RfPacketType>
  // 设置通讯协议设置
  setRFProtocol(protocol: RfPacketType): Promise<void>
  // 获取无线射频策略
  getWirelessRadioPolicy(): Promise<RfBoostSwitch>
  // 设置无线射频策略
  setWirelessRadioPolicy(policy: RfBoostSwitch): Promise<void>
  // 获取回报率
  getReportRate(): Promise<ReportRate>
  // 设置回报率
  setReportRate(rate: ReportRate): Promise<void>
  // 获取指定回报率对于的传感器模式
  getSensorMode(index: number): Promise<SensorMode>
  // 设置指定回报率对于的传感器模式
  setSensorMode(index: number, mode: SensorMode): Promise<void>
  // 获取全部传感器模式
  getSensorModeList(): Promise<SensorModes>
  // 设置全部传感器模式
  setSensorModeList(mode: SensorModes): Promise<void>
  // 获取扫描率
  getScanRate(): Promise<KeyScanRate>
  // 设置扫描率
  setScanRate(rate: KeyScanRate): Promise<void>

  // 获取移动同步
  getMoveSync(): Promise<MotionSynSwitch>
  // 设置移动同步
  setMoveSync(sync: MotionSynSwitch): Promise<void>
  // 获取直线/波纹修正
  getCorrect(): Promise<Correction>
  // 设置直线/波纹修正
  setCorrect(correct: Correction): Promise<void>
  // 获取休眠时间
  getSleepTime(): Promise<SleepTime>
  // 设置休眠时间
  setSleepTime(time: SleepTime): Promise<void>
  // 获取传感器角度
  getSensorAngle(): Promise<SensorAngle>
  // 设置传感器角度
  setSensorAngle(angle: SensorAngle): Promise<void>
  // 获取玻璃追踪技术
  getGlassTrack(): Promise<GlassSurfaceSwitch>
  // 设置玻璃追踪技术
  setGlassTrack(track: GlassSurfaceSwitch): Promise<void>
  // 获取按键按下去抖时间
  getDebounceTime(): Promise<KeyDebounceTime>
  // 设置按键按下去抖时间
  setDebounceTime(time: KeyDebounceTime): Promise<void>
  // 获取按键释放去抖时间
  getReleaseDebounceTime(): Promise<KeyReleaseDebounceTime>
  // 设置按键释放去抖时间
  setReleaseDebounceTime(time: KeyReleaseDebounceTime): Promise<void>
  // 获取静默高度
  getSilentHeight(): Promise<SilentHeight>
  // 设置静默高度
  setSilentHeight(height: SilentHeight): Promise<void>
  // 获取低电灯效提示
  getLowBatteryLight(): Promise<LowLightEffectSwitch>
  // 设置低电灯效提示
  setLowBatteryLight(light: LowLightEffectSwitch): Promise<void>
  // 获取DC 开关
  getDcSwitch(): Promise<DcSwitch>
  // 设置DC 开关
  setDcSwitch(dc: DcSwitch): Promise<void>

  // 获取所有配置
  getAllPerfCfg(): Promise<MousePerfCfg>
  // 设置所有配置
  setAllPerfCfg(cfg: MousePerfCfg): Promise<void>
}

/**
 * 多数芯片共用的回报率槽位顺序：125 / 250 / 500 / 1K / 2K / 4K / 8K 各占一个槽位。
 */
export const STANDARD_REPORT_RATE_SLOTS: readonly ReportRate[] = [
  0x08, 0x04, 0x02, 0x01, 0x84, 0x82, 0x81,
]

export class mousePerfCfgFeature extends FeatureBase implements IMousePerfCfgFeature {
  readonly reportRateSlots: readonly ReportRate[] = STANDARD_REPORT_RATE_SLOTS
  // 获取通讯协议设置
  getRFProtocol(): Promise<RfPacketType> {
    return this.unsupported('mousePerfCfgFeature.getRFProtocol')
  }
  // 设置通讯协议设置
  setRFProtocol(_protocol: RfPacketType): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setRFProtocol')
  }
  // 获取无线射频策略
  getWirelessRadioPolicy(): Promise<RfBoostSwitch> {
    return this.unsupported('mousePerfCfgFeature.getWirelessRadioPolicy')
  }
  // 设置无线射频策略
  setWirelessRadioPolicy(_policy: RfBoostSwitch): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setWirelessRadioPolicy')
  }
  // 获取回报率
  getReportRate(): Promise<ReportRate> {
    return this.unsupported('mousePerfCfgFeature.getReportRate')
  }
  // 设置回报率
  setReportRate(_rate: ReportRate): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setReportRate')
  }
  // 获取指定回报率对于的传感器模式
  getSensorMode(_index: number): Promise<SensorMode> {
    return this.unsupported('mousePerfCfgFeature.getSensorMode')
  }
  // 设置指定回报率对于的传感器模式
  setSensorMode(_index: number, _mode: SensorMode): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setSensorMode')
  }
  // 获取全部传感器模式
  getSensorModeList(): Promise<SensorModes> {
    return this.unsupported('mousePerfCfgFeature.getSensorModeList')
  }
  // 设置全部传感器模式
  setSensorModeList(_mode: SensorModes): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setSensorModeList')
  }
  // 获取扫描率
  getScanRate(): Promise<KeyScanRate> {
    return this.unsupported('mousePerfCfgFeature.getScanRate')
  }
  // 设置扫描率
  setScanRate(_rate: KeyScanRate): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setScanRate')
  }

  // 获取移动同步
  getMoveSync(): Promise<MotionSynSwitch> {
    return this.unsupported('mousePerfCfgFeature.getMoveSync')
  }
  // 设置移动同步
  setMoveSync(_sync: MotionSynSwitch): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setMoveSync')
  }
  // 获取直线修正
  getCorrect(): Promise<Correction> {
    return this.unsupported('mousePerfCfgFeature.getCorrect')
  }
  // 设置直线修正
  setCorrect(_correct: Correction): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setCorrect')
  }
  // 获取休眠时间
  getSleepTime(): Promise<SleepTime> {
    return this.unsupported('mousePerfCfgFeature.getSleepTime')
  }
  // 设置休眠时间
  setSleepTime(_time: SleepTime): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setSleepTime')
  }
  // 获取传感器角度
  getSensorAngle(): Promise<SensorAngle> {
    return this.unsupported('mousePerfCfgFeature.getSensorAngle')
  }
  // 设置传感器角度
  setSensorAngle(_angle: SensorAngle): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setSensorAngle')
  }
  // 获取玻璃追踪技术
  getGlassTrack(): Promise<GlassSurfaceSwitch> {
    return this.unsupported('mousePerfCfgFeature.getGlassTrack')
  }
  // 设置玻璃追踪技术
  setGlassTrack(_track: GlassSurfaceSwitch): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setGlassTrack')
  }
  // 获取按键按下去抖时间
  getDebounceTime(): Promise<KeyDebounceTime> {
    return this.unsupported('mousePerfCfgFeature.getDebounceTime')
  }
  // 设置按键按下去抖时间
  setDebounceTime(_time: KeyDebounceTime): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setDebounceTime')
  }
  // 获取按键释放去抖时间
  getReleaseDebounceTime(): Promise<KeyReleaseDebounceTime> {
    return this.unsupported('mousePerfCfgFeature.getReleaseDebounceTime')
  }
  // 设置按键释放去抖时间
  setReleaseDebounceTime(_time: KeyReleaseDebounceTime): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setReleaseDebounceTime')
  }
  // 获取静默高度
  getSilentHeight(): Promise<SilentHeight> {
    return this.unsupported('mousePerfCfgFeature.getSilentHeight')
  }
  // 设置静默高度
  setSilentHeight(_height: SilentHeight): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setSilentHeight')
  }
  // 获取低电灯效提示
  getLowBatteryLight(): Promise<LowLightEffectSwitch> {
    return this.unsupported('mousePerfCfgFeature.getLowBatteryLight')
  }
  // 设置低电灯效提示
  setLowBatteryLight(_light: LowLightEffectSwitch): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setLowBatteryLight')
  }
  // 获取DC 开关
  getDcSwitch(): Promise<DcSwitch> {
    return this.unsupported('mousePerfCfgFeature.getDcSwitch')
  }
  // 设置DC 开关
  setDcSwitch(_dc: DcSwitch): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setDcSwitch')
  }

  // 获取所有配置
  getAllPerfCfg(): Promise<MousePerfCfg> {
    return this.unsupported('mousePerfCfgFeature.getAllPerfCfg')
  }
  // 设置所有配置
  setAllPerfCfg(_cfg: MousePerfCfg): Promise<void> {
    return this.unsupported('mousePerfCfgFeature.setAllPerfCfg')
  }
}
